import React from 'react';
import { FormattedMessage } from '../../../../../util/reactIntl';
import css from './CurriculumOverview.module.css';

/**
 * Curriculum Overview — /p/curriculum-overview
 *
 * Self-contained section component. Content is hardcoded from the approved
 * Website Copy v1.0 document (4 Sep 2026). Rendered inside PageBuilder via
 * SectionContainer when the CMS section-id matches.
 *
 * DO NOT PUBLISH until the learning-system build (placement report, level
 * certificate, progress emails) is delivered and tested.
 */
const CurriculumOverview = () => {
  return (
    <article className={css.pageWrapper}>
      {/* ── Hero / Intro ── */}
      <header className={css.heroSection}>
        <h1 className={css.pageTitle}>
          <FormattedMessage
            id="CurriculumOverview.title"
            defaultMessage="A curriculum built for children who already have Arabic in the house"
          />
        </h1>
        <p className={css.introText}>
          <FormattedMessage
            id="CurriculumOverview.intro1"
            defaultMessage="Most Arabic courses are written for people starting from nothing. Heritage children are not starting from nothing. They understand their grandmother on the phone. They know the words for food, for family, for being told off. What they usually cannot do is read a sentence."
          />
        </p>
        <p className={css.introAccent}>
          <FormattedMessage
            id="CurriculumOverview.intro2"
            defaultMessage="Arabibi is built around that gap, not around a textbook."
          />
        </p>
      </header>

      {/* ── Five skills ── */}
      <section className={css.section}>
        <h2 className={css.sectionTitle}>
          <FormattedMessage
            id="CurriculumOverview.fiveSkills.title"
            defaultMessage="We measure five skills, not one level"
          />
        </h2>
        <p className={css.sectionText}>
          <FormattedMessage
            id="CurriculumOverview.fiveSkills.p1"
            defaultMessage={'A single "level" hides the thing that matters most. A child can understand a great deal of spoken Levantine and still be unable to read a single word. Putting one grade on that child tells a parent almost nothing, and tells a teacher less.'}
          />
        </p>
        <p className={css.sectionTextStrong}>
          <FormattedMessage
            id="CurriculumOverview.fiveSkills.p2"
            defaultMessage="So we track five skills separately:"
          />
        </p>

        <div className={css.tableWrapper}>
          <table className={css.table}>
            <thead>
              <tr>
                <th>
                  <FormattedMessage id="CurriculumOverview.table.skill" defaultMessage="Skill" />
                </th>
                <th>
                  <FormattedMessage id="CurriculumOverview.table.measures" defaultMessage="What it measures" />
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <FormattedMessage id="CurriculumOverview.skill.listening" defaultMessage="Listening" />
                </td>
                <td>
                  <FormattedMessage id="CurriculumOverview.skill.listening.desc" defaultMessage="Understanding spoken Arabic — in your family's dialect" />
                </td>
              </tr>
              <tr>
                <td>
                  <FormattedMessage id="CurriculumOverview.skill.speaking" defaultMessage="Speaking" />
                </td>
                <td>
                  <FormattedMessage id="CurriculumOverview.skill.speaking.desc" defaultMessage="Holding a conversation — in your family's dialect" />
                </td>
              </tr>
              <tr>
                <td>
                  <FormattedMessage id="CurriculumOverview.skill.reading" defaultMessage="Reading" />
                </td>
                <td>
                  <FormattedMessage id="CurriculumOverview.skill.reading.desc" defaultMessage="Understanding written Arabic — in Modern Standard Arabic" />
                </td>
              </tr>
              <tr>
                <td>
                  <FormattedMessage id="CurriculumOverview.skill.writing" defaultMessage="Writing" />
                </td>
                <td>
                  <FormattedMessage id="CurriculumOverview.skill.writing.desc" defaultMessage="Producing written Arabic — in Modern Standard Arabic" />
                </td>
              </tr>
              <tr>
                <td>
                  <FormattedMessage id="CurriculumOverview.skill.script" defaultMessage="Script" />
                </td>
                <td>
                  <FormattedMessage id="CurriculumOverview.skill.script.desc" defaultMessage="Decoding and handwriting the Arabic alphabet — the foundation that opens reading and writing" />
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Profile example */}
        <div className={css.profileExample}>
          <span className={css.profileLabel}>
            <FormattedMessage id="CurriculumOverview.profile.label" defaultMessage="A real child's profile looks like this:" />
          </span>
          <span className={css.profileLevels}>
            Listening B1 · Speaking A2 · Reading A1 · Writing A1 · Script Stage 2
          </span>
          <p className={css.profileExplanation}>
            <FormattedMessage
              id="CurriculumOverview.profile.explanation"
              defaultMessage="That is a picture a parent can actually use. It says: she understands us, she is finding her words, and she is just beginning to read."
            />
          </p>
        </div>
      </section>

      {/* ── CEFR levels ── */}
      <section className={css.section}>
        <h2 className={css.sectionTitle}>
          <FormattedMessage
            id="CurriculumOverview.cefr.title"
            defaultMessage="Our levels are CEFR levels"
          />
        </h2>
        <p className={css.sectionText}>
          <FormattedMessage
            id="CurriculumOverview.cefr.p1"
            defaultMessage="We did not invent our own scale. Arabibi uses the Common European Framework of Reference — A1, A2, B1, B2 — the same internationally recognised framework used across language education worldwide. For families in the United States we also cross-map to the ACTFL scale."
          />
        </p>

        <div className={css.tableWrapper}>
          <table className={css.table}>
            <thead>
              <tr>
                <th>
                  <FormattedMessage id="CurriculumOverview.cefr.level" defaultMessage="Level" />
                </th>
                <th>
                  <FormattedMessage id="CurriculumOverview.cefr.means" defaultMessage="What it means" />
                </th>
                <th>
                  <FormattedMessage id="CurriculumOverview.cefr.actfl" defaultMessage="Approx. ACTFL" />
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>A1 Breakthrough</td>
                <td>
                  <FormattedMessage id="CurriculumOverview.cefr.a1" defaultMessage="Basic familiar phrases. First contact with the language." />
                </td>
                <td>Novice Mid – Novice High</td>
              </tr>
              <tr>
                <td>A2 Waystage</td>
                <td>
                  <FormattedMessage id="CurriculumOverview.cefr.a2" defaultMessage="Simple routine exchanges about everyday things." />
                </td>
                <td>Intermediate Low – Intermediate Mid</td>
              </tr>
              <tr>
                <td>B1 Threshold</td>
                <td>
                  <FormattedMessage id="CurriculumOverview.cefr.b1" defaultMessage="Independent on familiar matters. Can hold their own." />
                </td>
                <td>Intermediate Mid – Intermediate High</td>
              </tr>
              <tr>
                <td>B2 Vantage</td>
                <td>
                  <FormattedMessage id="CurriculumOverview.cefr.b2" defaultMessage="Effective independent use, including some abstract topics." />
                </td>
                <td>Advanced Low – Advanced Mid</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className={css.highlightBox}>
          <FormattedMessage
            id="CurriculumOverview.cefr.whyItMatters"
            defaultMessage="Using a recognised framework matters. It means an Arabibi level can be explained to a school, compared to another language your child studies, and understood by anyone in education — rather than being a number that only means something inside our platform."
          />
        </div>
      </section>

      {/* ── Dialect & MSA ── */}
      <section className={css.section}>
        <h2 className={css.sectionTitle}>
          <FormattedMessage
            id="CurriculumOverview.dialect.title"
            defaultMessage="Dialect for speaking. Modern Standard Arabic for reading."
          />
        </h2>
        <p className={css.sectionText}>
          <FormattedMessage
            id="CurriculumOverview.dialect.p1"
            defaultMessage="Arabic is a diglossic language: what people speak at home is not what is written in books. Modern Standard Arabic is the language of news, literature and school. Almost nobody speaks it at the dinner table. Everyday life happens in a regional dialect — Levantine, Gulf, Egyptian."
          />
        </p>
        <p className={css.sectionText}>
          <FormattedMessage
            id="CurriculumOverview.dialect.p2"
            defaultMessage="Most Arabic programmes pretend this problem does not exist. It is the single most common reason they fail heritage children, who arrive already attached to a dialect and want to use it with the people they love."
          />
        </p>
        <p className={css.sectionTextStrong}>
          <FormattedMessage
            id="CurriculumOverview.dialect.p3"
            defaultMessage="Arabibi takes a position:"
          />
        </p>

        <div className={css.tableWrapper}>
          <table className={`${css.table} ${css.strandTable}`}>
            <thead>
              <tr>
                <th>
                  <FormattedMessage id="CurriculumOverview.strand.strand" defaultMessage="Strand" />
                </th>
                <th>
                  <FormattedMessage id="CurriculumOverview.strand.language" defaultMessage="Language" />
                </th>
                <th>
                  <FormattedMessage id="CurriculumOverview.strand.why" defaultMessage="Why" />
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <FormattedMessage id="CurriculumOverview.strand.listenSpeak" defaultMessage="Listening & Speaking" />
                </td>
                <td>
                  <FormattedMessage id="CurriculumOverview.strand.listenSpeak.lang" defaultMessage="Your family's dialect — Levantine, Gulf or Egyptian" />
                </td>
                <td>
                  <FormattedMessage id="CurriculumOverview.strand.listenSpeak.why" defaultMessage="This is the language of home and connection, and the place your child already has a foothold." />
                </td>
              </tr>
              <tr>
                <td>
                  <FormattedMessage id="CurriculumOverview.strand.readWriteScript" defaultMessage="Reading, Writing & Script" />
                </td>
                <td>
                  <FormattedMessage id="CurriculumOverview.strand.readWriteScript.lang" defaultMessage="Modern Standard Arabic" />
                </td>
                <td>
                  <FormattedMessage id="CurriculumOverview.strand.readWriteScript.why" defaultMessage="There is no standard written form of the dialects. Literacy in Arabic means literacy in MSA." />
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <p className={css.sectionText}>
          <FormattedMessage
            id="CurriculumOverview.dialect.bridging"
            defaultMessage="The two strands are connected, not separate. As children progress we deliberately bridge them: recognising that a word they say at home has a written form, reading MSA aloud, and eventually moving comfortably between the two."
          />
        </p>
      </section>

      {/* ── Script strand ── */}
      <section className={css.section}>
        <h2 className={css.sectionTitle}>
          <FormattedMessage
            id="CurriculumOverview.script.title"
            defaultMessage="The script strand — the barrier nobody talks about"
          />
        </h2>
        <p className={css.sectionText}>
          <FormattedMessage
            id="CurriculumOverview.script.p1"
            defaultMessage="For heritage learners the Arabic alphabet is usually the biggest obstacle, and it is almost completely separate from how well a child speaks. A fluent little speaker can be a complete beginner at reading, and that is normal."
          />
        </p>
        <p className={css.sectionTextStrong}>
          <FormattedMessage
            id="CurriculumOverview.script.p2"
            defaultMessage="We track script through five stages before it folds into reading and writing:"
          />
        </p>

        <div className={css.tableWrapper}>
          <table className={css.table}>
            <thead>
              <tr>
                <th>
                  <FormattedMessage id="CurriculumOverview.script.stage" defaultMessage="Stage" />
                </th>
                <th>
                  <FormattedMessage id="CurriculumOverview.script.focus" defaultMessage="Focus" />
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Stage 0 Pre-literacy</td>
                <td>
                  <FormattedMessage id="CurriculumOverview.script.s0" defaultMessage="Right-to-left orientation, and the Arabic sounds that have no English equivalent" />
                </td>
              </tr>
              <tr>
                <td>Stage 1 Letters</td>
                <td>
                  <FormattedMessage id="CurriculumOverview.script.s1" defaultMessage="All 28 letters, their sounds, and the dots that tell similar shapes apart" />
                </td>
              </tr>
              <tr>
                <td>Stage 2 Joining</td>
                <td>
                  <FormattedMessage id="CurriculumOverview.script.s2" defaultMessage="How letters change shape depending on where they sit in a word" />
                </td>
              </tr>
              <tr>
                <td>Stage 3 Vowels & fluency</td>
                <td>
                  <FormattedMessage id="CurriculumOverview.script.s3" defaultMessage="Short vowels, and reading vowelled text with confidence" />
                </td>
              </tr>
              <tr>
                <td>Stage 4 Independence</td>
                <td>
                  <FormattedMessage id="CurriculumOverview.script.s4" defaultMessage="Reading unvowelled text — the point at which real Arabic opens up" />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* ── How a child moves up ── */}
      <section className={css.section}>
        <h2 className={css.sectionTitle}>
          <FormattedMessage
            id="CurriculumOverview.levelUp.title"
            defaultMessage="How a child moves up a level"
          />
        </h2>
        <p className={css.sectionText}>
          <FormattedMessage
            id="CurriculumOverview.levelUp.p1"
            defaultMessage={'Every level is made of clear "I can" statements — for example, "I can understand simple instructions from my teacher", or "I can read a short vowelled sentence aloud". There are 99 of them across the framework.'}
          />
        </p>
        <p className={css.sectionText}>
          <FormattedMessage
            id="CurriculumOverview.levelUp.p2"
            defaultMessage="Your child's tutor marks these off as they are achieved, in the lesson report after each lesson. When a child has achieved around 80% of the statements in a skill, that skill becomes eligible for a level-up."
          />
        </p>
        <p className={css.sectionTextStrong}>
          <FormattedMessage
            id="CurriculumOverview.levelUp.p3"
            defaultMessage="A teacher then confirms it. Always."
          />
        </p>
        <p className={css.sectionText}>
          <FormattedMessage
            id="CurriculumOverview.levelUp.p4"
            defaultMessage="Progress is never awarded automatically by software. A qualified human being who has taught your child decides that they have moved up. We believe that is worth more to a parent than any algorithm — and it is the reason an Arabibi level means something."
          />
        </p>
      </section>

      {/* ── Ages and stages ── */}
      <section className={css.section}>
        <h2 className={css.sectionTitle}>
          <FormattedMessage
            id="CurriculumOverview.ages.title"
            defaultMessage="Ages and stages"
          />
        </h2>
        <p className={css.sectionText}>
          <FormattedMessage
            id="CurriculumOverview.ages.p1"
            defaultMessage="Arabibi is built first for heritage children of roughly 6 to 14, and the framework is designed around how that age learns. We also teach teenagers and adults — the same five skills, the same levels, the same dual-strand approach, with content chosen to suit the learner."
          />
        </p>
      </section>

      {/* ── Honesty closing ── */}
      <div className={css.honestySection}>
        <h2 className={css.honestyTitle}>
          <FormattedMessage
            id="CurriculumOverview.honesty.title"
            defaultMessage="Where we are honest with you"
          />
        </h2>
        <p className={css.honestyText}>
          <FormattedMessage
            id="CurriculumOverview.honesty.p1"
            defaultMessage="The Arabibi Framework is version 1.0. It is anchored to an internationally recognised scale, it is built on an explicit and defensible position on Arabic's diglossia, and it is designed from the ground up to be validated by specialists and to support accreditation in future."
          />
        </p>
        <p className={css.honestyText}>
          <FormattedMessage
            id="CurriculumOverview.honesty.p2"
            defaultMessage="That validation is work we are doing now, not a claim we are making today. When it is complete, we will say so here."
          />
        </p>
      </div>
    </article>
  );
};

export default CurriculumOverview;
